import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import {
    IconButton,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { ConfirmationDialog } from '@fnol/library/src/components';
import { GenericObject, getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { personType, riskTypeLife, translations } from '../../lib/consts';
import { IndividualCleanup } from '../Individual/Individual';
import { PaymentCleanup } from '../PaymentType/PaymentType';
import { validateIndividual } from '../../lib/customHooks';
import { AMLSectionCleanup } from '../AMLSection/AMLSection';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const createData = (name: string, payment: string, editable: boolean, editId?: number) => {
    return { name, payment, editable, editId };
};

interface UsersTableProps {
    inputPersonOpen: boolean;
    setInputPersonId: Dispatch<SetStateAction<number>>;
    setAddPossible: Dispatch<SetStateAction<boolean>>;
}

const UsersTable = ({ inputPersonOpen, setInputPersonId, setAddPossible }: UsersTableProps) => {
    const { values, setFieldValue } = useFormikContext<GenericObject>();
    const editIds = [1, 2, 3, 4, 5];

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(0);

    const rows: GenericObject[] = [];

    const validateUser = (id: number) => {
        const validateUserIndividual = validateIndividual(personType.user, id);
        return validateUserIndividual.isValid;
    };

    const copyUserPersonToId = (idFrom: number, idTo: number) => {
        setFieldValue(`user${idTo}Name`, values[`user${idFrom}Name`]);
        setFieldValue(`user${idTo}LastName`, values[`user${idFrom}LastName`]);
        setFieldValue(`user${idTo}DateOfBirth`, values[`user${idFrom}DateOfBirth`]);
        setFieldValue(`user${idTo}UCN`, values[`user${idFrom}UCN`]);
        setFieldValue(`user${idTo}Citizenship`, values[`user${idFrom}Citizenship`]);
        setFieldValue(`user${idTo}DualCitizenship`, values[`user${idFrom}DualCitizenship`]);
        setFieldValue(`user${idTo}Country`, values[`user${idFrom}Country`]);
        setFieldValue(`user${idTo}MyCountry`, values[`user${idFrom}MyCountry`]);
        setFieldValue(`user${idTo}Municipality`, values[`user${idFrom}Municipality`]);
        setFieldValue(`user${idTo}City`, values[`user${idFrom}City`]);
        setFieldValue(`user${idTo}CityInSerbia`, values[`user${idFrom}CityInSerbia`]);
        setFieldValue(`user${idTo}ZipCode`, values[`user${idFrom}ZipCode`]);
        setFieldValue(`user${idTo}Street`, values[`user${idFrom}Street`]);
        setFieldValue(`user${idTo}Phone`, values[`user${idFrom}Phone`]);
        setFieldValue(`user${idTo}Email`, values[`user${idFrom}Email`]);
        setFieldValue(`user${idTo}PersonalDocumentType`, values[`user${idFrom}PersonalDocumentType`]);
        setFieldValue(`user${idTo}PersonalDocumentNumber`, values[`user${idFrom}PersonalDocumentNumber`]);
        setFieldValue(`user${idTo}DocumentIssuePlace`, values[`user${idFrom}DocumentIssuePlace`]);
        setFieldValue(`user${idTo}DocumentIssueDate`, values[`user${idFrom}DocumentIssueDate`]);
        setFieldValue(`user${idTo}PlaceOfBirth`, values[`user${idFrom}PlaceOfBirth`]);
        setFieldValue(`user${idTo}Gender`, values[`user${idFrom}Gender`]);
        setFieldValue(`user${idTo}CurrentResidenceAddress`, values[`user${idFrom}CurrentResidenceAddress`]);
        setFieldValue(`user${idTo}CurrentResidenceStreet`, values[`user${idFrom}CurrentResidenceStreet`]);
        setFieldValue(`user${idTo}CurrentResidenceMunicipality`, values[`user${idFrom}CurrentResidenceMunicipality`]);
        setFieldValue(`user${idTo}CurrentResidenceCity`, values[`user${idFrom}CurrentResidenceCity`]);
        setFieldValue(`user${idTo}CurrentResidenceCityInSerbia`, values[`user${idFrom}CurrentResidenceCityInSerbia`]);
        setFieldValue(`user${idTo}CurrentResidenceZipCode`, values[`user${idFrom}CurrentResidenceZipCode`]);
        setFieldValue(`user${idTo}MailingAddress`, values[`user${idFrom}MailingAddress`]);
        setFieldValue(`user${idTo}MailingStreet`, values[`user${idFrom}MailingStreet`]);
        setFieldValue(`user${idTo}MailingMunicipality`, values[`user${idFrom}MailingMunicipality`]);
        setFieldValue(`user${idTo}MailingCity`, values[`user${idFrom}MailingCity`]);
        setFieldValue(`user${idTo}MailingCityInSerbia`, values[`user${idFrom}MailingCityInSerbia`]);
        setFieldValue(`user${idTo}MailingZipCode`, values[`user${idFrom}MailingZipCode`]);
        setFieldValue(`user${idTo}Bank`, values[`user${idFrom}Bank`]);
        setFieldValue(`user${idTo}BankName`, values[`user${idFrom}BankName`]);
        setFieldValue(`user${idTo}AccountNumber`, values[`user${idFrom}AccountNumber`]);
        setFieldValue(`user${idTo}Statement`, values[`user${idFrom}Statement`]);
        setFieldValue(`user${idTo}StatementNote`, values[`user${idFrom}StatementNote`]);

        setFieldValue(`thirdPerson${idTo}Name`, values[`thirdPerson${idFrom}Name`]);
        setFieldValue(`thirdPerson${idTo}LastName`, values[`thirdPerson${idFrom}LastName`]);
        setFieldValue(`thirdPerson${idTo}DateOfBirth`, values[`thirdPerson${idFrom}DateOfBirth`]);
        setFieldValue(`thirdPerson${idTo}UCN`, values[`thirdPerson${idFrom}UCN`]);
        setFieldValue(`thirdPerson${idTo}Citizenship`, values[`thirdPerson${idFrom}Citizenship`]);
        setFieldValue(`thirdPerson${idTo}DualCitizenship`, values[`thirdPerson${idFrom}DualCitizenship`]);
        setFieldValue(`thirdPerson${idTo}Country`, values[`thirdPerson${idFrom}Country`]);
        setFieldValue(`thirdPerson${idTo}MyCountry`, values[`thirdPerson${idFrom}MyCountry`]);
        setFieldValue(`thirdPerson${idTo}Municipality`, values[`thirdPerson${idFrom}Municipality`]);
        setFieldValue(`thirdPerson${idTo}City`, values[`thirdPerson${idFrom}City`]);
        setFieldValue(`thirdPerson${idTo}CityInSerbia`, values[`thirdPerson${idFrom}CityInSerbia`]);
        setFieldValue(`thirdPerson${idTo}ZipCode`, values[`thirdPerson${idFrom}ZipCode`]);
        setFieldValue(`thirdPerson${idTo}Street`, values[`thirdPerson${idFrom}Street`]);
        setFieldValue(`thirdPerson${idTo}Phone`, values[`thirdPerson${idFrom}Phone`]);
        setFieldValue(`thirdPerson${idTo}Email`, values[`thirdPerson${idFrom}Email`]);
        setFieldValue(`thirdPerson${idTo}PersonalDocumentType`, values[`thirdPerson${idFrom}PersonalDocumentType`]);
        setFieldValue(`thirdPerson${idTo}PersonalDocumentNumber`, values[`thirdPerson${idFrom}PersonalDocumentNumber`]);
        setFieldValue(`thirdPerson${idTo}DocumentIssuePlace`, values[`thirdPerson${idFrom}DocumentIssuePlace`]);
        setFieldValue(`thirdPerson${idTo}DocumentIssueDate`, values[`thirdPerson${idFrom}DocumentIssueDate`]);
        setFieldValue(`thirdPerson${idTo}PlaceOfBirth`, values[`thirdPerson${idFrom}PlaceOfBirth`]);
        setFieldValue(`thirdPerson${idTo}Gender`, values[`thirdPerson${idFrom}Gender`]);
        setFieldValue(
            `thirdPerson${idTo}CurrentResidenceAddress`,
            values[`thirdPerson${idFrom}CurrentResidenceAddress`],
        );
        setFieldValue(`thirdPerson${idTo}CurrentResidenceStreet`, values[`thirdPerson${idFrom}CurrentResidenceStreet`]);
        setFieldValue(
            `thirdPerson${idTo}CurrentResidenceMunicipality`,
            values[`thirdPerson${idFrom}CurrentResidenceMunicipality`],
        );
        setFieldValue(`thirdPerson${idTo}CurrentResidenceCity`, values[`thirdPerson${idFrom}CurrentResidenceCity`]);
        setFieldValue(
            `thirdPerson${idTo}CurrentResidenceCityInSerbia`,
            values[`thirdPerson${idFrom}CurrentResidenceCityInSerbia`],
        );
        setFieldValue(
            `thirdPerson${idTo}CurrentResidenceZipCode`,
            values[`thirdPerson${idFrom}CurrentResidenceZipCode`],
        );
        setFieldValue(`thirdPerson${idTo}MailingAddress`, values[`thirdPerson${idFrom}MailingAddress`]);
        setFieldValue(`thirdPerson${idTo}MailingStreet`, values[`thirdPerson${idFrom}MailingStreet`]);
        setFieldValue(`thirdPerson${idTo}MailingMunicipality`, values[`thirdPerson${idFrom}MailingMunicipality`]);
        setFieldValue(`thirdPerson${idTo}MailingCity`, values[`thirdPerson${idFrom}MailingCity`]);
        setFieldValue(`thirdPerson${idTo}MailingCityInSerbia`, values[`thirdPerson${idFrom}MailingCityInSerbia`]);
        setFieldValue(`thirdPerson${idTo}MailingZipCode`, values[`thirdPerson${idFrom}MailingZipCode`]);
        setFieldValue(`thirdPerson${idTo}Bank`, values[`thirdPerson${idFrom}Bank`]);
        setFieldValue(`thirdPerson${idTo}BankName`, values[`thirdPerson${idFrom}BankName`]);
        setFieldValue(`thirdPerson${idTo}AccountNumber`, values[`thirdPerson${idFrom}AccountNumber`]);
        setFieldValue(`thirdPerson${idTo}Statement`, values[`thirdPerson${idFrom}Statement`]);
        setFieldValue(`thirdPerson${idTo}StatementNote`, values[`thirdPerson${idFrom}StatementNote`]);
        setFieldValue(`paymentToUser${idTo}`, values[`paymentToUser${idFrom}`]);
        if (getIsSalesForce(getApplicationId())) {
            setFieldValue(`user${idTo}AttorneySignature`, values[`user${idFrom}AttorneySignature`]);
        }
    };

    const handleEditUserPerson = (editId: number) => {
        setFieldValue('userName', values[`user${editId}Name`]);
        setFieldValue('userLastName', values[`user${editId}LastName`]);
        setFieldValue('userDateOfBirth', values[`user${editId}DateOfBirth`]);
        setFieldValue('userUCN', values[`user${editId}UCN`]);
        setFieldValue('userCitizenship', values[`user${editId}Citizenship`]);
        setFieldValue('userDualCitizenship', values[`user${editId}DualCitizenship`]);
        setFieldValue('userCountry', values[`user${editId}Country`]);
        setFieldValue('userMyCountry', values[`user${editId}MyCountry`]);
        setFieldValue('userMunicipality', values[`user${editId}Municipality`]);
        setFieldValue('userCity', values[`user${editId}City`]);
        setFieldValue('userCityInSerbia', values[`user${editId}CityInSerbia`]);
        setFieldValue('userZipCode', values[`user${editId}ZipCode`]);
        setFieldValue('userStreet', values[`user${editId}Street`]);
        setFieldValue('userPhone', values[`user${editId}Phone`]);
        setFieldValue('userEmail', values[`user${editId}Email`]);
        setFieldValue('userPersonalDocumentType', values[`user${editId}PersonalDocumentType`]);
        setFieldValue('userPersonalDocumentNumber', values[`user${editId}PersonalDocumentNumber`]);
        setFieldValue('userDocumentIssuePlace', values[`user${editId}DocumentIssuePlace`]);
        setFieldValue('userDocumentIssueDate', values[`user${editId}DocumentIssueDate`]);
        setFieldValue('userPlaceOfBirth', values[`user${editId}PlaceOfBirth`]);
        setFieldValue('userGender', values[`user${editId}Gender`]);
        setFieldValue('userCurrentResidenceAddress', values[`user${editId}CurrentResidenceAddress`]);
        setFieldValue('userCurrentResidenceStreet', values[`user${editId}CurrentResidenceStreet`]);
        setFieldValue('userCurrentResidenceMunicipality', values[`user${editId}CurrentResidenceMunicipality`]);
        setFieldValue('userCurrentResidenceCity', values[`user${editId}CurrentResidenceCity`]);
        setFieldValue('userCurrentResidenceCityInSerbia', values[`user${editId}CurrentResidenceCityInSerbia`]);
        setFieldValue('userCurrentResidenceZipCode', values[`user${editId}CurrentResidenceZipCode`]);
        setFieldValue('userMailingAddress', values[`user${editId}MailingAddress`]);
        setFieldValue('userMailingStreet', values[`user${editId}MailingStreet`]);
        setFieldValue('userMailingMunicipality', values[`user${editId}MailingMunicipality`]);
        setFieldValue('userMailingCity', values[`user${editId}MailingCity`]);
        setFieldValue('userMailingCityInSerbia', values[`user${editId}MailingCityInSerbia`]);
        setFieldValue('userMailingZipCode', values[`user${editId}MailingZipCode`]);
        setFieldValue('userStatement', values[`user${editId}Statement`]);
        setFieldValue('userStatementNote', values[`user${editId}StatementNote`]);
        if (getIsSalesForce(getApplicationId())) {
            setFieldValue('userAttorneySignature', values[`user${editId}AttorneySignature`]);
        }
        setInputPersonId(editId);
        setFieldValue('inputPersonOpen', true);
    };

    const handleDeleteUserPerson = (deleteId: number) => {
        editIds.map((editId) => {
            if (editId === deleteId) {
                IndividualCleanup(personType.user, setFieldValue, deleteId);
                IndividualCleanup(personType.thirdPerson, setFieldValue, deleteId);
                PaymentCleanup(personType.thirdPerson, setFieldValue, deleteId);
                PaymentCleanup(personType.user, setFieldValue, deleteId);
                AMLSectionCleanup(personType.user, setFieldValue, deleteId);
                setAddPossible(true);
                let counter = editId;
                while (counter <= editIds.length) {
                    const oneUp = counter + 1;
                    if (values[`user${oneUp}Name`]) {
                        copyUserPersonToId(oneUp, counter);
                        IndividualCleanup(personType.user, setFieldValue, oneUp);
                        IndividualCleanup(personType.thirdPerson, setFieldValue, oneUp);
                        PaymentCleanup(personType.user, setFieldValue, oneUp);
                        AMLSectionCleanup(personType.user, setFieldValue, oneUp);
                    }
                    counter++;
                }
            }
        });
    };

    if (values.isSubmitterUser === 'true' && !values.risks.includes(riskTypeLife.outlive)) {
        rows.push(createData(`${values.submitterName} ${values.submitterLastName}`, `paymentToUser`, false));
    }

    editIds.map((editId) => {
        if (values[`user${editId}Name`]) {
            rows.push(
                createData(
                    `${values[`user${editId}Name`]} ${values[`user${editId}LastName`]}`,
                    `paymentToUser${editId}`,
                    true,
                    editId,
                ),
            );
        }
    });

    if (rows.length === 5) {
        setAddPossible(false);
    }

    useEffect(() => {
        setFieldValue('usersFromTable', rows);
    }, [rows.length]);

    return (
        <>
            <TableContainer component={Paper}>
                {rows.length > 0 && (
                    <Table sx={{ minWidth: 230 }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>{translations.users.USERS_LIST}</StyledTableCell>
                                <StyledTableCell align="right"> </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <StyledTableRow key={row.id ? row.id : row.name}>
                                    <StyledTableCell component="th" scope="row">
                                        <span>{row.name}</span>
                                        <span style={{ color: '#c21b17', paddingLeft: '30px' }}>
                                            {!validateUser(row.editId) && values.risks.includes(riskTypeLife.outlive)
                                                ? translations.users.ADD_USER_DATA_OUTLIVE
                                                : null}
                                        </span>
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        {row.editable && (
                                            <>
                                                <IconButton
                                                    onClick={() => handleEditUserPerson(row.editId)}
                                                    aria-label="edit"
                                                    disabled={inputPersonOpen}
                                                >
                                                    <Edit />
                                                </IconButton>
                                                {!values.risks.includes(riskTypeLife.outlive) && (
                                                    <IconButton
                                                        onClick={() => {
                                                            setDeleteId(row.editId);
                                                            setConfirmOpen(true);
                                                        }}
                                                        aria-label="delete"
                                                        disabled={inputPersonOpen}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                )}
                                            </>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <ConfirmationDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={() => handleDeleteUserPerson(deleteId)}
            >
                {translations.CONFIRM_DELETE}
            </ConfirmationDialog>
        </>
    );
};

export default UsersTable;
